import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Calculator, Pill, Activity, GitBranch, ClipboardList, BookOpen, Syringe, Trophy, MapPin } from 'lucide-react';
const features = [{
  icon: ClipboardList,
  label: 'Casos clínicos'
}, {
  icon: FileText,
  label: 'Cheatsheets'
}, {
  icon: Pill,
  label: 'Tablas de drogas'
}, {
  icon: Activity,
  label: 'Escalas clínicas'
}, {
  icon: Calculator,
  label: 'Calculadoras'
}, {
  icon: GitBranch,
  label: 'Diagramas'
}, {
  icon: GitBranch,
  label: 'Algoritmos'
}, {
  icon: Syringe,
  label: 'Procedimientos'
}];
const conditions = ['Arritmias y ECG', 'Síndrome coronario agudo', 'Insuficiencia cardíaca', 'Crisis hipertensiva', 'Tromboembolismo pulmonar', 'Accidente cerebrovascular', 'Convulsiones y epilepsia', 'Shock séptico, cardiogénico e hipovolémico', 'Cetoacidosis diabética', 'Intoxicaciones frecuentes'];
export function SystemOverview() {
  return <section className="py-16 px-4 bg-white relative overflow-hidden" data-id="element-300">
      <div className="max-w-5xl mx-auto" data-id="element-301">
        <div className="text-center mb-10" data-id="element-302">
          <div className="inline-flex items-center gap-2 bg-accent-gold text-medical-blue font-bold px-4 py-1.5 rounded-full text-xs mb-4 tracking-widest shadow-md" data-id="element-303">
            <Trophy size={14} data-id="element-304" />
            SISTEMA N°1 EN ARGENTINA
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-gray-900 mb-4 max-w-3xl mx-auto leading-tight" data-id="element-305">
            El sistema visual de urgencias para{' '}
            <span className="text-medical-blue" data-id="element-306">estudiantes argentinos</span>
          </h2>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8" data-id="element-307">
          {[{
          num: '+240',
          label: 'páginas'
        }, {
          num: '80+',
          label: 'algoritmos'
        }, {
          num: '6',
          label: 'tomos'
        }].map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-gradient-to-br from-medical-blue to-blue-800 rounded-2xl p-4 md:p-6 text-center shadow-xl text-white" data-id="element-308">
              <div className="text-3xl md:text-5xl font-extrabold mb-1 font-heading" data-id="element-309">
                {stat.num}
              </div>
              <div className="text-[10px] md:text-xs font-bold text-blue-200 uppercase tracking-widest" data-id="element-310">
                {stat.label}
              </div>
            </motion.div>)}
        </div>

        {/* Topic Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-id="element-311">
          {['ECG', 'Cardiovascular', 'Neurológico', 'Shock', 'Laboratorio', 'Procedimientos'].map((topic, i) => <motion.span key={i} initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className="bg-medical-blue text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-bold shadow-md" data-id="element-312">
              {topic}
            </motion.span>)}
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12" data-id="element-313">
          {features.map((feat, i) => {
          const Icon = feat.icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-2 md:gap-3 shadow-sm hover:shadow-md hover:border-medical-blue/30 transition-all" data-id="element-314">
                <div className="bg-medical-blue/10 p-2 rounded-lg text-medical-blue shrink-0" data-id="element-315">
                  <Icon size={18} data-id="element-316" />
                </div>
                <span className="text-xs md:text-sm font-semibold text-gray-700 leading-tight" data-id="element-317">
                  {feat.label}
                </span>
              </motion.div>;
        })}
        </div>

        {/* Conditions Covered */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100" data-id="element-318">
          <div className="flex items-start gap-3 mb-6" data-id="element-319">
            <div className="bg-medical-blue text-white p-2.5 rounded-xl shrink-0" data-id="element-320">
              <MapPin size={20} data-id="element-321" />
            </div>
            <div data-id="element-322">
              <h3 className="font-heading font-bold text-lg md:text-xl text-gray-900 leading-tight" data-id="element-323">
                Funciona desde tu primera guardia
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-324">
                En cualquier hospital de Argentina
              </p>
            </div>
          </div>

          <p className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider" data-id="element-325">
            Condiciones cubiertas:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6" data-id="element-326">
            {conditions.map((cond, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.03
          }} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-100" data-id="element-327">
                <div className="w-2 h-2 rounded-full bg-urgency-red shrink-0" data-id="element-328"></div>
                <span className="text-gray-700 text-sm font-medium" data-id="element-329">
                  {cond}
                </span>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}